import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { MenuItem } from "src/types/menu/menuTypes";

type AuthState = {
  isLoggedIn: boolean;
  menuTree: MenuItem[];

  setLogin: (token: string, menuTree?: MenuItem[]) => void;
  setMenuTree: (menuTree: MenuItem[]) => void;
  logout: () => void;
};

const isDev = process.env.NODE_ENV === "development";

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        isLoggedIn: false,
        menuTree: [],

        setLogin: (token, menuTree) =>
          set(
            {
              isLoggedIn: true,
              menuTree: menuTree ?? [],
            },
            false,
            "auth/setLogin"
          ),

        setMenuTree: (menuTree) =>
          set(
            {
              menuTree,
            },
            false,
            "auth/setMenuTree"
          ),

        logout: () =>
          set(
            {
              isLoggedIn: false,
              menuTree: [],
            },
            false,
            "auth/logout"
          ),
      }),
      {
        name: "auth-storage",
        partialize: (state) => ({ menuTree: state.menuTree }),
      }
    ),
    {
      name: "auth-store",
      enabled: isDev,
    }
  )
);

// 메뉴 트리에서 모든 라우터 링크를 평탄화하여 추출
export function flattenMenuRoutes(menuTree: MenuItem[]): string[] {
  const routes: string[] = [];

  function traverse(items: MenuItem[]) {
    for (const item of items) {
      if (item.menuRouterLink && item.menuRouterLink !== "/") {
        routes.push(item.menuRouterLink);
      }
      if (item.children?.length) {
        traverse(item.children);
      }
    }
  }

  traverse(menuTree);
  return routes;
}

// 특정 경로가 메뉴 트리에 있는지 확인
export function isRouteAllowed(menuTree: MenuItem[], pathname: string): boolean {
  // 항상 허용되는 경로들
  const alwaysAllowed = ["/", "/login", "/error"];
  if (alwaysAllowed.some((p) => pathname === p || pathname.startsWith("/error"))) {
    return true;
  }

  const allowedRoutes = flattenMenuRoutes(menuTree);

  // 정확히 일치하거나 하위 경로인 경우 허용
  return allowedRoutes.some((route) => {
    if (route === "/") return false;
    return pathname === route || pathname.startsWith(route + "/");
  });
}
